import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useSettings } from "../lib/SettingsContext";

export const Footer = () => {
  const { settings } = useSettings();
  const hasContact = settings.phone || settings.email || settings.address;
  const hasSocial = settings.instagram || settings.facebook || settings.linkedin;

  return (
    <footer data-testid="site-footer" className="bg-antracite text-avorio">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5 space-y-6">
            <Logo dark />
            <p className="text-avorio/60 text-sm leading-relaxed max-w-sm">
              Manutenzione professionale degli impianti.
              <br />
              Tre servizi specializzati. Un unico referente.
            </p>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-bronzo">
              Mendrisiotto · Ticino
            </p>
          </div>

          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-pietra mb-5">Navigazione</p>
            <ul className="space-y-3 text-sm">
              {[
                { to: "/servizi", label: "Servizi" },
                { to: "/prezzi", label: "Prezzi" },
                { to: "/amministrazioni", label: "Amministrazioni immobiliari" },
                { to: "/contatti", label: "Contatti" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-avorio/70 hover:text-bronzo transition-colors duration-300">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4 space-y-8">
            {hasContact && (
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-pietra mb-5">Contatti</p>
                <ul className="space-y-3 text-sm">
                  {settings.phone && (
                    <li>
                      <a
                        href={`tel:${settings.phone.replace(/\s/g, "")}`}
                        data-testid="footer-phone-link"
                        className="inline-flex items-center gap-3 text-avorio/70 hover:text-bronzo transition-colors duration-300"
                      >
                        <Phone className="w-4 h-4 text-bronzo" /> {settings.phone}
                      </a>
                    </li>
                  )}
                  {settings.email && (
                    <li>
                      <a
                        href={`mailto:${settings.email}`}
                        data-testid="footer-email-link"
                        className="inline-flex items-center gap-3 text-avorio/70 hover:text-bronzo transition-colors duration-300"
                      >
                        <Mail className="w-4 h-4 text-bronzo" /> {settings.email}
                      </a>
                    </li>
                  )}
                  {settings.address && (
                    <li className="inline-flex items-start gap-3 text-avorio/70">
                      <MapPin className="w-4 h-4 text-bronzo mt-0.5 shrink-0" />
                      <span>{settings.address}</span>
                    </li>
                  )}
                </ul>
              </div>
            )}
            {hasSocial && (
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.25em] text-pietra mb-5">Seguici</p>
                <ul className="space-y-3 text-sm">
                  {[
                    { url: settings.instagram, label: "Instagram", testid: "footer-instagram" },
                    { url: settings.facebook, label: "Facebook", testid: "footer-facebook" },
                    { url: settings.linkedin, label: "LinkedIn", testid: "footer-linkedin" },
                  ]
                    .filter((s) => s.url)
                    .map((s) => (
                      <li key={s.label}>
                        <a
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={s.testid}
                          className="inline-flex items-center gap-2 text-avorio/70 hover:text-bronzo transition-colors duration-300"
                        >
                          {s.label} <ArrowUpRight className="w-3.5 h-3.5" />
                        </a>
                      </li>
                    ))}
                </ul>
              </div>
            )}
            <Link
              to="/contatti"
              data-testid="footer-cta-intervento"
              className="inline-flex items-center gap-2 bg-bronzo text-white text-sm font-medium px-6 py-3 hover:bg-avorio hover:text-antracite transition-colors duration-300"
            >
              Richiedi un intervento
            </Link>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-avorio/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-avorio/40 text-xs">
            © {new Date().getFullYear()} V32 Group · V32 Manutenzioni Tecniche — Mendrisiotto, Canton Ticino
          </p>
          <div className="flex items-center gap-6 text-xs">
            <Link to="/privacy" data-testid="footer-privacy-link" className="text-avorio/40 hover:text-bronzo transition-colors duration-300">
              Privacy Policy
            </Link>
            <Link to="/note-legali" data-testid="footer-legal-link" className="text-avorio/40 hover:text-bronzo transition-colors duration-300">
              Note legali
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
